import { memo } from 'react';
import { Box, Flex, Heading, Spacer, Switch, Text } from '@chakra-ui/react';
import {
  DisplayAggregateQuestionDto,
  DisplayIndividualQuestionDto,
} from '@keyops-cep/api-client';

import Visualization from '../../../components/Visualization';
import useComponentFlag from '../../../hooks/useComponentFlag';
import { COMPONENTS } from '../../../utils/componentDisplayFlags';
import { getPrevAvgAnnotation } from '../../../utils/functions/visualization-utils';
import i18n from '../../../utils/i18n';
import { AnswerDataWithDemographic } from '../types';

type VisualizationDisplayProps = {
  question: DisplayIndividualQuestionDto | DisplayAggregateQuestionDto;
  questionAnswers: AnswerDataWithDemographic[];
  displayPrevAvg: boolean;
  useQuestionComparativeModal: boolean;
  togglePrevAvgDisplay: () => void;
  displayDataTable?: boolean;
  toggleDataTableDisplay?: () => void;
  displayEditToggle?: boolean;
  toggleEditDisplay?: () => void;
};

export const VisualizationDisplay = memo(
  ({
    question,
    questionAnswers,
    displayPrevAvg,
    useQuestionComparativeModal,
    togglePrevAvgDisplay,
    displayDataTable,
    toggleDataTableDisplay,
    displayEditToggle,
    toggleEditDisplay,
  }: VisualizationDisplayProps) => {
    const prevAvgAnnotation = getPrevAvgAnnotation(question);
    const showPrevAvgToggle = useComponentFlag(COMPONENTS.PREV_AVG_TOGGLE);

    return (
      <Box
        p={{ base: 1, md: 4 }}
        h={'95%'}
        mx={'auto'}
        justifyContent={'space-between'}
      >
        <Flex
          alignItems={'center'}
          visibility={{ base: 'hidden', md: 'visible' }}
          mb={2.5}
        >
          <Heading>
            {i18n.t('engagementDetails.questionCards.visualizationHeading')}
          </Heading>
          {!!prevAvgAnnotation && showPrevAvgToggle && (
            <>
              <Spacer />
              <Box mr={8}>
                <Switch
                  id="display-prev-avg"
                  size="sm"
                  isChecked={displayPrevAvg}
                  onChange={togglePrevAvgDisplay}
                />{' '}
                <Text display={'inline'} fontSize="sm">
                  {i18n.t('engagementDetails.questionCards.showPrevAvg')}
                </Text>
              </Box>
            </>
          )}
        </Flex>
        <Visualization
          question={question}
          questionAnswers={questionAnswers}
          displayPrevAvg={displayPrevAvg}
          togglePrevAvgDisplay={togglePrevAvgDisplay}
          useQuestionComparativeModal={useQuestionComparativeModal}
          displayDataTable={displayDataTable}
          toggleDataTableDisplay={toggleDataTableDisplay}
          displayEditToggle={displayEditToggle}
          toggleEditDisplay={toggleEditDisplay}
        />
      </Box>
    );
  }
);
VisualizationDisplay.displayName = 'VisualizationDisplay';
